<script setup lang="ts">
import SpData from '@/assets/SpData.svg';
import Intake from '../../../assets/Intake.xlsx?url';
import FollowUp from '../../../assets/Follow-up.xlsx?url';
import Health from '../../../assets/Health.xlsx?url';
import { LocalStorageUtils, routeConstants } from '@/utils';
import {
  ButtonType,
  CustomButton,
  IconPosition,
  OnboardingOption,
  OnboardingTemplate
} from '@/components';
import ToggleSwitch from 'primevue/toggleswitch';
import InputText from 'primevue/inputtext';
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import { ref } from 'vue';
import { useStudyStore } from '@/stores/study';
import { useRouter } from 'vue-router';

const studyStore = useStudyStore();
const router = useRouter();
const activeStep = ref(1);
const startType = ref(0);
const templateType = ref(0);
const studyName = ref(`${LocalStorageUtils.getFirstName()}'s first study`);
const caseManagement = ref(true);

const onBackClick = () => {
  activeStep.value--;
};

const incrementStep = () => {
  activeStep.value++;
};

const selectStartType = (type: number) => {
  startType.value = type;
  incrementStep();
};

const selectTemplate = (template: number) => {
  templateType.value = template;
};

const startScratch = () => {
  startType.value = 2;
  activeStep.value = 3;
};

const createStudy = () => {
  studyStore.createStudy({title: studyName.value, long: caseManagement.value ? 'long' : 'cross'});
};

const demoStudy = async () => {
  await studyStore.createStudy({title: studyName.value, long: templateType.value == 1 ? 'long' : 'cross'}, false);
  if (templateType.value == 1) {
    await studyStore.importStudyForm('Intake.xlsx', Intake);
    await studyStore.importStudyForm('Follow-up.xlsx', FollowUp);
  } else if (templateType.value == 2) {
    await studyStore.importStudyForm('Health.xlsx', Health);
  }
  await router.push({ name: routeConstants.DASHBOARD.name });
};
</script>

<template>
  <div class="onboarding-root">
    <div class="onboarding-paper">
      <div class="paper-root">
        <Stepper v-model:value="activeStep" class="basis-[40rem]">
          <div class="step-header">
            <div class="back-button" v-if="activeStep > 1">
              <CustomButton text="Back"
                :type="ButtonType.WITHOUT_OUTLINE"
                :icon="{ icon: 'chevron-left', style: 'far' }"
                @onClick="onBackClick"
                class="button"/>
            </div>
            <StepList>
              <Step v-slot="{ activateCallback, a11yAttrs }" asChild :value="1">
                <div class="step-root" v-bind="a11yAttrs.root">
                  <button :class="`circle-shape ${
                      activeStep == 1 ? 'selected-circle' : ''
                    }`"
                    @click="activateCallback"
                    v-bind="a11yAttrs.header"/>
                </div>
              </Step>
              <Step v-slot="{ activateCallback, a11yAttrs }" asChild :value="2">
                <div class="step-root" v-bind="a11yAttrs.root">
                  <button :class="`circle-shape ${
                      activeStep == 2 ? 'selected-circle' : ''
                    }`"
                    @click="activateCallback"
                    v-bind="a11yAttrs.header"/>
                </div>
              </Step>
              <Step v-slot="{ activateCallback, a11yAttrs }" asChild :value="3">
                <div class="step-root" v-bind="a11yAttrs.root">
                  <button :class="`circle-shape ${
                      activeStep == 3 ? 'selected-circle' : ''
                    }`"
                    @click="activateCallback"
                    v-bind="a11yAttrs.header"/>
                </div>
              </Step>
            </StepList>
          </div>

          <StepPanels>
            <StepPanel :value="1">
              <div class="step-1">
                <img alt="Studypages Data" :src="SpData" class="logo" />

                <h1 class="header">
                  Welcome to Studypage Data, <br />{{
                    LocalStorageUtils.getFirstName()
                  }}!
                </h1>

                <p class="sub-header">
                  Let's help you build your first study and collect some data.
                </p>

                <CustomButton text="Let's go"
                  :type="ButtonType.POSITIVE"
                  :fontWeight="600"
                  @onClick="incrementStep"
                  class="button"/>
              </div>
            </StepPanel>
            <StepPanel :value="2">
              <div class="step-1">
                <h1 class="header">How would you like to start?</h1>

                <div class="start-options">
                  <OnboardingOption :icon="{ style: 'fad', icon: 'sparkles' }"
                    title="Start with a template"
                    @itemClick="selectStartType(1)"
                    subTitle="Use pre-built forms"/>
                  <OnboardingOption :icon="{ style: 'fad', icon: 'pen-to-square' }"
                    title="Customize your own form"
                    @itemClick="selectStartType(2)"
                    subTitle="Start from scrach"/>
                </div>
              </div>
            </StepPanel>
            <StepPanel :value="3">
              <div class="step-1">
                <div class="select-template" v-if="startType == 1">
                  <h1 class="header">Choose a template</h1>
                  <div class="templates">
                    <OnboardingTemplate title="Manage cases"
                      subTitle="Manage cases and follow up on them across time."
                      @templateClick="selectTemplate(1)"
                      :selected="templateType == 1"
                      imgSrc="https://uploads-ssl.webflow.com/5fb6849ed98f565962345f5c/600460fc97ff633bb6ee81de_manage%20cases.svg"/>
                    <OnboardingTemplate title="Self-reported data"
                      subTitle="Keep track your symptoms with a daily journal."
                      @templateClick="selectTemplate(2)"
                      :selected="templateType == 2"
                      imgSrc="https://uploads-ssl.webflow.com/5fb6849ed98f565962345f5c/6004610e043fbc826d6a9176_old%20lady%20phone.svg"/>
                  </div>

                  <CustomButton text="Start collecting data"
                    :type="ButtonType.POSITIVE"
                    :fontWeight="600"
                    :disabled="templateType == 0"
                    :loading="studyStore.studyLoader.isLoading"
                    @onClick="demoStudy"
                    class="collect-button"/>

                  <CustomButton text="Skip and start from a blank form"
                    :type="ButtonType.WITHOUT_OUTLINE"
                    @onClick="startScratch"
                    class="collect-button"/>
                </div>

                <div class="start-scratch" v-if="startType == 2">
                  <h1 class="header">Let's create your first study</h1>

                  <div class="left">
                    <div class="study-name">
                      <div class="study-name-label">Study Name</div>
                      <InputText v-model="studyName"
                        class="study-name-input"
                        placeholder="Enter the name of your study"/>
                      <div class="study-name-error">
                        <span v-if="studyName === ''">Invalid study name</span>
                      </div>
                    </div>
                    <div class="case-management">
                      <div class="case-manage-label">Case Management</div>
                      <div class="case-manage-description">
                        Cases act as folders and they help you organize data
                        entries
                      </div>
                      <div class="case-management-bar">
                        <div class="switch-container">
                          <ToggleSwitch v-model="caseManagement" />
                          <label class="switch-label">ON</label>
                        </div>

                        <div class="case-management-change-text">
                          {{
                            caseManagement
                              ? "Create cases and share them with your team."
                              : "Turn case management OFF when collecting self-reported data (e.g. patient-reported outcomes)."
                          }}
                        </div>
                      </div>
                    </div>
                    <div class="footer-buttons">
                        <CustomButton text="Cancel"
                            :type="ButtonType.OUTLINED"
                            @onClick="onBackClick"/>
                        <CustomButton text="Go to form builder"
                            :icon="{style: 'far', icon: 'chevron-right', position: IconPosition.END}"
                            :type="ButtonType.POSITIVE"
                            :fontWeight="600"
                            :margin-left="32"
                            :loading="studyStore.studyLoader.isLoading"
                            :disabled="studyName === ''"
                            @onClick="createStudy"/>
                    </div>
                  </div>
                </div>
              </div>
            </StepPanel>
          </StepPanels>
        </Stepper>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../assets/variables.module";
.onboarding-root {
  display: flex;
  justify-content: center;
  padding: 80px;
  height: 100vh;
  width: 100%;
  overflow: auto;
}
.onboarding-paper {
  width: 750px;
  max-height: calc(100vh - 160px);
  margin: auto 0;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 0 8px 2px rgba(0, 0, 0, 0.15);
  overflow: auto;
}
.paper-root {
  display: flex;
  flex-direction: column;
  padding: 40px 60px;
}
.step-header {
  position: relative;
  display: flex;
  align-items: center;

  .back-button {
    position: absolute;
    z-index: 1;
  }
}
:deep(.p-steplist) {
  width: 100%;
  justify-content: center;
}
.step-root {
  margin: 0 12px;
}
.step-1 {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    margin: 24px 0;
  }

  h1 {
    text-align: center;
    margin: 36px 0;
    font-weight: 700;
  }

  p {
    font-weight: 500;
    margin-bottom: 36px;
  }

  button {
    min-width: 150px;
  }
}
.circle-shape {
  border-radius: 50%;
  border: none;
  background-color: rgba(0, 0, 0, 0.12);
  height: 15px;
  width: 15px;
  display: flex;
  align-items: center;
}
.selected-circle {
  background-color: $primary-color;
}
.start-options {
  width: 100%;
  display: flex;
  justify-content: space-between;
}
.select-template {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  h1 {
    margin-bottom: 24px;
  }

  .templates {
    width: 100%;
    display: flex;
    justify-content: space-around;
  }

  .collect-button {
    min-width: 400px;
    margin-top: 28px;
  }
}
.start-scratch {
  width: 100%;
  padding: 0 24px;

  .study-name {
    display: flex;
    flex-direction: column;
    margin: 10px 0;

    .study-name-label {
      font-size: 16px;
      font-weight: 700;
      line-height: 30px;
      padding-bottom: 8px;
    }

    input {
      width: 100%;
      font-size: 14px;
      font-weight: 500;
    }

    .study-name-error {
      display: flex;
      justify-content: flex-start;
      span {
        color: red;
      }
    }
  }

  .case-management {
    display: flex;
    flex-direction: column;
    margin: 10px 0;
    padding: 40px 0 60px 0;

    .case-manage-label {
      font-size: 16px;
      font-weight: 700;
      line-height: 30px;
    }

    .case-manage-description {
      color: #575e66;
      font-size: 14px;
      line-height: 20px;
      margin-bottom: 20px;
    }

    .case-management-bar {
      display: flex;
      align-items: center;
      text-align: center;
      flex-direction: row;
    }

    .switch-container {
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #ced4da;
      border-radius: 4px;
      padding: 12px 10px;
      box-sizing: border-box;
      margin-right: 32px;

      .switch-label {
        margin-left: 12px;
      }
    }

    .case-management-change-text {
      text-align: left;
      justify-content: center;
      min-height: 28px;
      max-width: 400px;
      line-height: 14px;
    }
  }

  .footer-buttons {
    display: flex;
    justify-content: flex-end;

    button {
        min-width: unset;
    }
  }
}
</style>
